import { useState, useEffect } from "react";
import mitt from "mitt";

const scrollEmitter: mitt.Emitter = mitt();
const SCROLL_EVENT = "scroll";
const SCROLL_TO_EVENT = "scrollTo";

/**
 * eventManager - A manager for toggling an event on and off.
 * @see https://github.com/developit/mitt for more emitter API details.
 *
 * @example
 * // Define an event handler.
 * const onScroll = eventManager("scroll");
 *
 * // Pass the event handler a callback to handle the expected data stream.
 * // The event handler, when invoked, returns an optional handler to remove the event.
 * // Typically the event removal should happen in componentWillUnmount.
 * const scrollDepth = ({scrollDepth}) => console.log(`Current scroll depth - ${scrollDepth}`));
 * const removeOnScrollListener = onScroll(scrollDepth);
 *
 * // Cleanup the event listener when is it no longer needed.
 * removerOnScrollListener();
 */
const eventManager = (event: string) => (cb: (props: any) => void) => {
  scrollEmitter.on(event, cb);
  return () => scrollEmitter.off(event, cb);
};

/**
 * emitOnScroll - publishes the scrolling event for subscribers to listen to.
 */
const emitOnScroll = (scrollDepth: number) => {
  scrollEmitter.emit(SCROLL_EVENT, { scrollDepth });
};

/**
 * onScroll - subscribes to the scroll event.
 */
const onScroll = eventManager(SCROLL_EVENT);

/**
 * emitOnScrollTo - publishes the scrollTo event for subscribers to listen to.
 */
const emitScrollTo = (
  targetPosition: number,
  scrollRate: undefined | number,
) => {
  scrollEmitter.emit(SCROLL_TO_EVENT, { targetPosition, scrollRate });
};

/**
 * onScrollTo - subscribes to the scrollTo event.
 */
const onScrollTo = eventManager(SCROLL_TO_EVENT);

/**
 * useScrollTo hook.
 */
const useOnScroll = () => {
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const removeScrollListener = onScroll(({ scrollDepth }) =>
      setDepth(scrollDepth),
    );

    return function cleanup() {
      removeScrollListener();
    };
  }, []);

  return depth;
};

export {
  scrollEmitter,
  eventManager,
  emitOnScroll,
  onScroll,
  useOnScroll,
  emitScrollTo,
  onScrollTo,
  SCROLL_EVENT,
  SCROLL_TO_EVENT,
};
